// Transaction Status Constants based on backend models (common_api / mobile_api)

// Transaction Status Codes (common_api.db.models.TerminalTransaction)
export const TRANSACTION_STATUS = {
  2: 'take_battery_success',
  3: 'take_battery_error',
  5: 'return_battery_success',
  15: 'battery_lost',
  17: 'return_battery_unlocked',
  19: 'validation_success',
  21: 'timeouted',
  23: 'canceled_by_customer'
}

// Payment Status Codes (common_api.db.models.Payment)
export const PAYMENT_STATUS = {
  1: 'processing',
  2: 'approved',
  3: 'concluded',
  4: 'rejected',
  5: 'cancelled',
  6: 'refunded',
  10: 'pending',
  11: 'authorized',
  12: 'captured'
}

// Pre-Payment Status Codes (common_api.db.models.PrePayment -> Payment)
export const PRE_PAYMENT_STATUS = {
  1: 'processing',
  2: 'approved',
  4: 'rejected',
  5: 'cancelled',
  6: 'refunded',
  10: 'pending',
  11: 'authorized'
}

// Pre-Payment Type Codes (common_api.db.models.PaymentMethod)
export const PRE_PAYMENT_TYPE = {
  1: 'credit_card',
  2: 'debit_card',
  3: 'pix'
}

// Payment Transaction Status strings (used in payment.status)
export const PAYMENT_TRANSACTION_STATUS = {
  Approved: 'approved',
  Captured: 'captured',
  Partial: 'partial',
  Refunded: 'refunded',
  Voided: 'voided'
}

// Status categories for filtering
export const STATUS_CATEGORY = {
  ALL: 'all',
  COMPLETED: 'completed',
  PENDING: 'pending',
  REFUNDED: 'refunded',
  ISSUES: 'issues'
}

// Status display configuration
export const STATUS_DISPLAY = {
  transaction: {
    take_battery_success: { variant: 'success', icon: 'check-circle' },
    battery_lost: { variant: 'error', icon: 'xmark-circle' },
    return_battery_unlocked: { variant: 'success', icon: 'check-circle' },
    validation_success: { variant: 'success', icon: 'check-circle' },
    timeouted: { variant: 'warning', icon: 'clock' },
    take_battery_error: { variant: 'error', icon: 'xmark-circle' },
    2: { variant: 'success', icon: 'check-circle' },
    15: { variant: 'error', icon: 'xmark-circle' },
    17: { variant: 'success', icon: 'check-circle' },
    19: { variant: 'success', icon: 'check-circle' }
  },
  payment: {
    pending: { variant: 'warning', icon: 'clock' },
    processing: { variant: 'info', icon: 'spinner' },
    authorized: { variant: 'info', icon: 'shield-halved' },
    approved: { variant: 'success', icon: 'check-circle' },
    captured: { variant: 'success', icon: 'check-circle' },
    refunded: { variant: 'info', icon: 'rotate-left' },
    cancelled: { variant: 'neutral', icon: 'ban' },
    rejected: { variant: 'error', icon: 'xmark-circle' },
    1: { variant: 'info', icon: 'spinner' },
    2: { variant: 'success', icon: 'check-circle' },
    4: { variant: 'error', icon: 'xmark-circle' },
    5: { variant: 'neutral', icon: 'ban' },
    6: { variant: 'info', icon: 'rotate-left' },
    11: { variant: 'info', icon: 'shield-halved' },
    12: { variant: 'success', icon: 'check-circle' }
  },
  prePayment: {
    pending: { variant: 'warning', icon: 'clock' },
    processing: { variant: 'info', icon: 'spinner' },
    authorized: { variant: 'info', icon: 'shield-halved' },
    approved: { variant: 'success', icon: 'check-circle' },
    refunded: { variant: 'info', icon: 'rotate-left' },
    cancelled: { variant: 'neutral', icon: 'ban' },
    rejected: { variant: 'error', icon: 'xmark-circle' },
    1: { variant: 'info', icon: 'spinner' },
    2: { variant: 'success', icon: 'check-circle' },
    4: { variant: 'error', icon: 'xmark-circle' },
    5: { variant: 'neutral', icon: 'ban' },
    6: { variant: 'info', icon: 'rotate-left' },
    11: { variant: 'info', icon: 'shield-halved' }
  }
}

const ISSUE_TRANSACTION_STATUSES = ['battery_lost', 'timeouted', 'take_battery_error', 'canceled_by_customer']

const ISSUE_PAYMENT_STATUSES = [
  'rejected',
  'cancelled',
  'failed',
  4, // Payment.REJECTED
  5 // Payment.CANCELLED
]

// Helper function to determine transaction category
export function getTransactionCategory(transaction) {
  if (isTransactionRefunded(transaction)) {
    return STATUS_CATEGORY.REFUNDED
  }

  if (transaction?.return_time === 0) {
    return STATUS_CATEGORY.PENDING
  }

  const txStatus = transaction?.status
  const preStatus = transaction?.last_pre_payment?.status
  const payStatus = transaction?.payment?.status

  const hasIssue =
    ISSUE_TRANSACTION_STATUSES.includes(txStatus) ||
    ISSUE_PAYMENT_STATUSES.includes(preStatus) ||
    ISSUE_PAYMENT_STATUSES.includes(payStatus)

  if (hasIssue) {
    return STATUS_CATEGORY.ISSUES
  }

  return STATUS_CATEGORY.COMPLETED
}

// Helper function to get display info for a status
export function getStatusDisplay(type, code) {
  const key = typeof code === 'string' ? code.toLowerCase() : code
  return STATUS_DISPLAY[type]?.[key] || { variant: 'neutral', icon: 'circle-info' }
}

/**
 * Verifica se a transação possui reembolso agendado / em processamento
 */
export function isRefundScheduled(transaction) {
  return transaction?.last_pre_payment?.status === 'processing' || transaction?.payment?.status === 'processing'
}

/**
 * Verifica se a transação já teve o reembolso/estorno concluído
 */
export function isRefundCompleted(transaction) {
  const pre = transaction?.last_pre_payment?.status
  const pay = transaction?.payment?.status
  return pre === 'refunded' || pre === 'cancelled' || pay === 'refunded' || pay === 'cancelled'
}

/**
 * Verifica se a transação teve reembolso solicitado, agendado ou concluído
 */
export function isTransactionRefunded(transaction) {
  return isRefundCompleted(transaction) || isRefundScheduled(transaction)
}
